<script lang="ts" setup>
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import dayjs from "dayjs";
import type { VForm } from "vuetify/components";
import DatePicker from "@/components/pickers/datePicker/DatePicker.vue";
import TimePicker from "@/components/pickers/TimePicker.vue";
import OmniSelect from "@/components/selects/OmniSelect.vue";
import TimezoneSelect from "@/components/selects/TimezoneSelect.vue";
import BolNumberHandler from "@/views/trips/components/BolNumberHandler.vue";
import SealsHandler from "@/views/trips/components/SealsHandler.vue";
import FileUploadingGrid from "@/views/trips/components/FileUploadingGrid.vue";
import ReloadPickupBolInfo from "@/views/trips/components/ReloadPickupBolInfo.vue";
import { API } from "@/api/api";
import { useBaseView, useDateTime } from "@/composables";
import { useError } from "@/composables/useError";
import { useSeals } from "@/composables/useSeals";
import { useRouteStopFiles } from "@/composables/useRouteStopFiles";
import { useValidation } from "@/composables/useValidation";
import { OrderFreightType, OrderStopType } from "@/constants/order";
import { RouteStopFileType, RouteStopStatus } from "@/constants/trip";
import { toVerifyBol } from "@/utils/bol";
import { toInt } from "@/utils/number";
import BolValidator from "@/validations/bol.validator";
import SealValidator from "@/validations/seal.validator";
import type { Bol, RouteStopDto, TravelOrderDto, TravelOrderOrderInfo } from "@/api/trips/dto/trip.dto";
import type { StopInfoFormProps, VuetifyRule } from "@/types/props";
import type { PickupInfoFormData, StopInfoFreight, StopInfoSeal, TripRouteFreight, TripRouteStop } from "@/types/trip";

const { t } = useI18n();

const loading = defineModel<boolean>("loading", { default: false });

const props = withDefaults(defineProps<StopInfoFormProps>(), {
  preload: false,
  readonly: false,
});

const emits = defineEmits<{
  (e: "save"): void;
}>();

const api = new API();

const { dateToUTC, timezonesShortList } = useDateTime();
const { uniqueArray } = useBaseView();
const { requiredValidator, digitsValidator } = useValidation();
const { getTravelOrdersSeals, saveSeals } = useSeals();
const { showAPIError, scrollToError } = useError();

const formRef = useTemplateRef<VForm | null>("form");
const loadData = ref<PickupInfoFormData>({
  bols: [],
  bolNotAvailable: false,
  seals: [],
  actualFreights: [],
  files: [],
  date: "",
  time: "",
  timezone: "",
});

const routeStop = ref<TripRouteStop | RouteStopDto>(props.stop);
const isLoading = ref<boolean>(false);
const isSaving = ref<boolean>(false);
const isBolsValidated = ref<boolean>(false);
const isSealsValidated = ref<boolean>(false);
const isBolEditing = ref<boolean>(false);
const isSealsEditing = ref<boolean>(false);
const initialSeals = ref<StopInfoSeal[]>([]);

const isStopLoaded = computed<boolean>(() =>
  [RouteStopStatus.waitingForVerification, RouteStopStatus.waitingForGoodToGo, RouteStopStatus.completed].includes(
    routeStop.value.status,
  ),
);

const allocatedBolIds = computed<string[]>(() => {
  return [...new Set(loadData.value.actualFreights.flatMap((freight) => freight.bolIds ?? []))];
});

const isStopVerified = computed<boolean>(() =>
  [RouteStopStatus.waitingForGoodToGo, RouteStopStatus.completed].includes(routeStop.value.status),
);

const isBolReadonly = computed<boolean>(() => props.readonly && !isBolEditing.value);

const routeStops = computed<(TripRouteStop | RouteStopDto)[]>(() => props.route.routeStops);

const stopTravelOrders = computed<TravelOrderDto[]>(() => {
  return props.route.travelOrders.filter((travelOrder) => {
    if (!travelOrder.id) {
      return false;
    }

    return routeStop.value.travelOrderIds?.includes(travelOrder.id);
  });
});

const { loadFiles, saveFiles } = useRouteStopFiles(
  () => loadData.value.files,
  () => props.tripId,
  () => routeStop.value.id,
  RouteStopFileType.bol,
);

const isSealsDisabled = computed<boolean>(() => isStopLoadedAndVerified.value && !isSealsEditing.value);

const relatedOrder = computed<TravelOrderOrderInfo | null>(() => {
  return (
    props.route.travelOrders.find((to) => {
      if (!to.id) {
        return null;
      }

      return routeStop.value.travelOrderIds?.includes(to.id);
    })?.order || null
  );
});

const reusedBols = computed<Bol[]>(() => {
  return routeStop.value.bols;
});

const freights = computed<TripRouteFreight[]>(
  () =>
    uniqueArray(
      props.route.freights.filter((f) => routeStop.value.freights.includes(f.id)),
      ["id"],
    ) as TripRouteFreight[],
);

const stopFreights = computed<TripRouteFreight[]>(() =>
  routeStop.value.freights.map((sf) => freights.value.find((f) => f.id === sf)).filter((f) => f !== undefined),
);

const stopBols = computed<Bol[]>(() => {
  const bolIds = stopFreights.value.flatMap((f) => f.bolIds ?? []);

  return routeStop.value.bols.filter((bol) => bolIds.includes(bol.id));
});

const actualPalletsQuantity = computed(() =>
  loadData.value.actualFreights.reduce(
    (sum, f) => (f.type === OrderFreightType.pallets ? sum + toInt(f.quantity) : sum),
    0,
  ),
);

const palletsQuantity = computed(() =>
  freights.value.reduce((sum, f) => (f.type === OrderFreightType.pallets ? sum + toInt(f.plannedQuantity) : sum), 0),
);

const actualPiecesQuantity = computed(() =>
  loadData.value.actualFreights.reduce(
    (sum, f) => (f.type === OrderFreightType.pieces ? sum + toInt(f.quantity) : sum),
    0,
  ),
);

const piecesQuantity = computed(() =>
  freights.value.reduce((sum, f) => (f.type === OrderFreightType.pieces ? sum + toInt(f.plannedQuantity) : sum), 0),
);

const actualFreightsWeight = computed(() => loadData.value.actualFreights.reduce((sum, f) => sum + toInt(f.weight), 0));

const freightsWeight = computed(() => freights.value.reduce((sum, f) => sum + toInt(f.plannedWeight), 0));

const actualPalletsQuantityClass = computed(() =>
  getComparisonClass(actualPalletsQuantity.value, palletsQuantity.value),
);

const actualPiecesQuantityClass = computed(() => getComparisonClass(actualPiecesQuantity.value, piecesQuantity.value));

const actualFreightsWeightClass = computed(() => getComparisonClass(actualFreightsWeight.value, freightsWeight.value));

const filledBols = computed<Bol[]>(() => {
  return loadData.value.bols.filter((bol) => {
    return bol !== null;
  });
});

const activeBols = computed<Bol[]>(() => {
  return filledBols.value.filter((bol) => {
    return !bol.isDeleted;
  });
});

const bolFreightOptions = computed(() => {
  return createBolOptions(isReload.value ? routeStop.value.bols : activeBols.value);
});

const bolFileOptions = computed(() => {
  return activeBols.value.map((bol) => {
    return {
      name: bol.number,
      id: bol.number,
    };
  });
});

const isFreightBolRequired = computed<boolean>(() => {
  return !loadData.value.bolNotAvailable && !isReload.value;
});

const freightBolRules = computed<VuetifyRule[]>(() => {
  return isFreightBolRequired.value ? [requiredValidator] : [];
});

const bolErrors = computed<(string | null)[]>(() => {
  if (!isBolsValidated.value || isReload.value) {
    return [];
  }

  return new BolValidator(loadData.value.bols, loadData.value.actualFreights).validate();
});

const sealErrors = computed<Record<string, string | null>>(() => {
  if (!isSealsValidated.value) {
    return {};
  }

  return new SealValidator(loadData.value.seals).validate();
});

const hasSealErrors = computed<boolean>(() => {
  return Object.values(sealErrors.value).some(Boolean);
});

const hasBols = computed(() => {
  return activeBols.value.length > 0;
});

const singleBol = computed<Bol | null>(() => {
  return activeBols.value[0] ?? null;
});

const freightsQuantity = computed(() => {
  const result = [];
  if (palletsQuantity.value > 0) {
    result.push(palletsQuantity.value + " plt");
  }
  if (piecesQuantity.value > 0) {
    result.push(piecesQuantity.value + " pcs");
  }
  return result.join(", ");
});

const isStopLoadedAndVerified = computed(() => {
  const statusesToShow = [RouteStopStatus.waitingForGoodToGo, RouteStopStatus.completed];
  return statusesToShow.includes(routeStop.value.status);
});

const isReload = computed(() => {
  return props.stop.isReload;
});

const isFileDisabled = computed(() => {
  if (!props.readonly) {
    return false;
  }

  return !isBolEditing.value;
});

function createBolOptions(bols: Bol[]) {
  return bols.map((bol) => {
    return {
      name: bol.number,
      id: bol.id,
    };
  });
}

async function applyBol(): Promise<void> {
  isBolsValidated.value = true;

  const result = await formRef.value?.validate();

  if (!result?.valid || bolErrors.value.some(Boolean)) {
    await nextTick();
    scrollToError(formRef.value?.$el);

    return;
  }

  isBolEditing.value = false;
}

function initSeals(): void {
  loadData.value.seals = getTravelOrdersSeals(stopTravelOrders.value);
  initialSeals.value = loadData.value.seals.map((seal) => ({ ...seal }));
}

async function insertSeals(): Promise<boolean> {
  const result = await saveSeals(props.tripId, loadData.value.seals, initialSeals.value);

  loadData.value.seals = result.seals;
  initialSeals.value = result.initialSeals.map((seal) => ({ ...seal }));

  return result.isSaved;
}

async function applySeals(): Promise<void> {
  isSealsValidated.value = true;

  if (hasSealErrors.value) {
    await nextTick();
    scrollToError(formRef.value?.$el);

    return;
  }

  const isSaved = await insertSeals();

  if (!isSaved) {
    return;
  }

  isSealsEditing.value = false;
}

function onSealDeleted(deletedSeal: StopInfoSeal): void {
  initialSeals.value = initialSeals.value.filter((seal) => seal.id !== deletedSeal.id);
}

async function toggleSealsEditing(): Promise<void> {
  if (!isSealsEditing.value) {
    isSealsEditing.value = true;

    return;
  }

  await applySeals();
}

async function toggleBolEditing(): Promise<void> {
  if (!isBolEditing.value) {
    isBolEditing.value = true;

    return;
  }

  await applyBol();
}

function getComparisonClass(actual: number, planned: number): string {
  if (actual > planned) {
    return "text-error";
  } else if (actual < planned) {
    return "text-warning";
  } else {
    return "text-success";
  }
}

function getFreightDeliveryPoint(freight: StopInfoFreight): string | null {
  const facility = routeStops.value.find(
    (o) => o.type === OrderStopType.delivery && o.freights.includes(freight.id),
  )?.facility;
  return facility ? facility.addressLine + ", " + facility.address : null;
}

function getFreightData(freight: StopInfoFreight): string {
  return freight.plannedQuantity + " " + getFreightType(freight) + ", " + freight.plannedWeight + " lbs";
}

function getFreightType(freight: StopInfoFreight): string {
  if (freight.type === OrderFreightType.pallets) return "plt";
  return "pcs";
}

function removeMissingBols(): void {
  const bolIds = bolFreightOptions.value.map((option) => option.id);
  const deletedBolNumbers = filledBols.value.filter((bol) => bol.isDeleted).map((bol) => bol.number);

  loadData.value.actualFreights = loadData.value.actualFreights.map((freight) => {
    return {
      ...freight,
      bolIds: freight.bolIds?.filter((bolId) => bolIds.includes(bolId)),
    };
  });

  loadData.value.files = loadData.value.files.map((file) => {
    return {
      ...file,
      bol: file.bol && deletedBolNumbers.includes(file.bol) ? undefined : file.bol,
    };
  });
}

function setBolsAutomatically(): void {
  const bolRows = loadData.value.bols.filter((bol) => !bol?.isDeleted);

  if (bolRows.length > 1 && hasBols.value) {
    return;
  }

  const bolId = singleBol.value?.id;
  const bolNumber = singleBol.value?.number;

  if (loadData.value.files.length) {
    loadData.value.files = loadData.value.files.map((file) => {
      return {
        ...file,
        bol: bolNumber,
      };
    });
  }

  loadData.value.actualFreights = loadData.value.actualFreights.map((freight) => {
    return {
      ...freight,
      bolIds: bolId ? [bolId] : [],
    };
  });
}

async function loadRouteStop(): Promise<void> {
  if (!props.stop.id) {
    return;
  }

  try {
    const response = await api.trips.getRouteStop(props.tripId, String(props.stop.id));

    if (response.success && response.data) {
      routeStop.value = response.data;
    } else {
      showAPIError(response, t("trip.route-stop-loading-failed"));
    }
  } catch (error) {
    console.error(error);
  }
}

async function init(): Promise<void> {
  let freightNumber = 1;
  loadData.value.actualFreights = stopFreights.value.map((f) => ({
    ...f,
    weight: f.weight || f.plannedWeight,
    quantity: f.quantity || f.plannedQuantity,
    number: freightNumber++,
  }));

  loadData.value.bols = stopBols.value.length ? [...stopBols.value] : [null];

  if (!props.preload) {
    loadData.value.timezone = routeStop.value.facility?.timezone ?? "";
    const date = dayjs().tz(loadData.value.timezone);
    loadData.value.date = date.format("YYYY-MM-DD");
    loadData.value.time = date.format("HH:mm");

    return;
  }

  loadData.value.timezone = routeStop.value.timezone ?? "";
  const date = dayjs(routeStop.value.loadUnloadTime).tz(loadData.value.timezone);
  loadData.value.date = date.format("YYYY-MM-DD");
  loadData.value.time = date.format("HH:mm");
  loadData.value.bolNotAvailable = isStopLoaded.value && !stopBols.value.length && !isReload.value;

  loadData.value.files = await loadFiles();
}

async function validateForm(): Promise<boolean> {
  isBolsValidated.value = true;
  isSealsValidated.value = true;

  const result = await formRef.value?.validate();

  if (!result?.valid || bolErrors.value.some(Boolean) || hasSealErrors.value) {
    await nextTick();
    scrollToError(formRef.value?.$el);

    return false;
  }

  return true;
}

async function loadOrVerifyRouteStop(): Promise<boolean> {
  const stopId = String(routeStop.value.id);
  const verifyPayload = {
    bols: isReload.value ? [] : filledBols.value.map((bol) => toVerifyBol(bol, stopId)),
    freights: loadData.value.actualFreights.map((f) => {
      return {
        id: f.id,
        bolIds: f.bolIds,
      };
    }),
  };

  const loadedPayload = {
    bols: isReload.value ? [] : filledBols.value,
    freights: loadData.value.actualFreights,
  };

  try {
    const response = isStopVerified.value
      ? await api.trips.verifyRouteStop(props.tripId, stopId, verifyPayload)
      : await api.trips.loadRouteStop(props.tripId, stopId, {
          ...loadedPayload,
          loadTime: dateToUTC(loadData.value.date + " " + loadData.value.time, loadData.value.timezone),
        });

    if (!response.success) {
      showAPIError(
        response,
        isStopVerified.value ? t("trip.verify-update-failed") : t("trip.load-verification-failed"),
      );

      return false;
    }

    return true;
  } catch (error) {
    console.error(error);

    return false;
  }
}

async function save(): Promise<void> {
  const isValid = await validateForm();

  if (!isValid) {
    return;
  }

  isSaving.value = true;

  try {
    const isSealsSaved = await insertSeals();

    if (!isSealsSaved) {
      return;
    }

    const isRouteStopSaved = await loadOrVerifyRouteStop();

    if (!isRouteStopSaved) {
      return;
    }

    await saveFiles();

    emits("save");
  } catch (error) {
    console.error(error);
  } finally {
    isSaving.value = false;
  }
}

watch([isLoading, isSaving], () => {
  loading.value = isLoading.value || isSaving.value;
});

watch(
  () => loadData.value.bols,
  () => {
    removeMissingBols();
    setBolsAutomatically();
  },
  { deep: true },
);

onMounted(async () => {
  isLoading.value = true;

  await loadRouteStop();
  await init();
  initSeals();

  isLoading.value = false;
});

defineExpose({ save });
</script>

<template>
  <v-form ref="form">
    <template v-if="!isReload">
      <div class="mb-6">
        <div class="d-flex align-center justify-space-between">
          <div class="font-size-16 font-weight-500 mb-4 text-grey-navy">BOL info</div>

          <v-btn
            v-if="isStopLoadedAndVerified"
            :prepend-icon="isBolEditing ? 'mdi-check' : 'mdi-pencil'"
            :variant="isBolEditing ? 'flat' : 'outlined'"
            color="primary"
            data-qa="edit-bol-button"
            density="comfortable"
            @click="toggleBolEditing"
          >
            {{ isBolEditing ? t("general.apply") : t("trip.edit-bol") }}
          </v-btn>
        </div>

        <bol-number-handler
          v-model="loadData.bols"
          v-model:not-available="loadData.bolNotAvailable"
          :errors="bolErrors"
          :order="relatedOrder"
          :readonly="isBolReadonly"
          :reused-bols="reusedBols"
          :route-stop="routeStop"
          :route-stops="routeStops"
        />
      </div>

      <div v-if="stopTravelOrders.length" class="mb-6">
        <div class="d-flex align-center justify-end">
          <v-btn
            v-if="isStopLoadedAndVerified"
            :prepend-icon="isSealsEditing ? 'mdi-check' : 'mdi-pencil'"
            :variant="isSealsEditing ? 'flat' : 'outlined'"
            color="primary"
            data-qa="edit-seals-button"
            density="comfortable"
            @click="toggleSealsEditing"
          >
            {{ isSealsEditing ? t("general.apply") : t("trip.edit-seals") }}
          </v-btn>
        </div>

        <seals-handler
          v-model="loadData.seals"
          :disabled="isSealsDisabled"
          :errors="sealErrors"
          :route-stops="routeStops"
          :travel-orders="stopTravelOrders"
          :trip-id="props.tripId"
          @deleted="onSealDeleted"
        />
      </div>

      <div class="mb-6">
        <div class="font-size-16 font-weight-500 mb-4 text-grey-navy">
          Files<span v-if="!loadData.bolNotAvailable" class="text-error">*</span>
        </div>

        <file-uploading-grid
          v-model="loadData.files"
          :disabled="isFileDisabled"
          :required="hasBols"
          @load="setBolsAutomatically"
        >
          <template #footer="{ file }">
            <omni-select
              v-if="file"
              v-model="file.bol"
              :disabled="isBolReadonly || loadData.bolNotAvailable"
              :items="bolFileOptions"
              :required="!loadData.bolNotAvailable"
              :rules="loadData.bolNotAvailable ? [] : [requiredValidator]"
              class="mt-5"
              data-qa="bol-select-file-zone"
              label="BOL"
            ></omni-select>
          </template>
        </file-uploading-grid>
      </div>
    </template>

    <template v-else-if="!isLoading">
      <div class="mb-6">
        <reload-pickup-bol-info :allocated-bol-ids="allocatedBolIds" :bols="routeStop.bols" />
      </div>
    </template>

    <div class="mb-6">
      <div class="font-size-16 font-weight-500 mb-4 text-grey-navy">Freights at stop:</div>
      <div v-for="actualFreight in loadData.actualFreights" :key="actualFreight.id">
        <div class="bg-grey-lighten-4 pa-4">
          <div class="text-grey-darken-3 font-weight-500 mb-2">Freight#{{ actualFreight.number }}:</div>
          <div class="d-flex align-center justify-space-between">
            <div>
              <span class="text-grey-darken-2 font-weight-500">Planned: </span>
              <span class="text-grey-darken-3 font-weight-500" data-qa="freight-block-planned"
                >#{{ actualFreight.number }}: {{ getFreightData(actualFreight) }}</span
              >
            </div>
            <div class="d-flex align-center text-grey-darken-1">
              <v-icon class="mr-2" color="error" icon="mdi-map-marker-radius-outline" size="18"></v-icon>
              #{{ actualFreight.number }}: {{ getFreightDeliveryPoint(actualFreight) }}
            </div>
          </div>
        </div>
        <div class="bg-grey-lighten-5 pa-4">
          <div class="text-grey-darken-3 font-weight-500 mb-3">Actual:</div>
          <v-row>
            <v-col>
              <v-text-field
                v-model="actualFreight.quantity"
                :disabled="readonly"
                :rules="[requiredValidator, digitsValidator]"
                autocomplete="off"
                class="required bg-white"
                color="primary"
                data-qa="quantity-field"
                density="compact"
                hide-details="auto"
                label="Quantity"
                variant="outlined"
              />
            </v-col>
            <v-col>
              <v-text-field
                v-model="actualFreight.weight"
                :disabled="readonly"
                :rules="[requiredValidator, digitsValidator]"
                autocomplete="off"
                class="required bg-white"
                color="primary"
                data-qa="total-weight-field"
                density="compact"
                hide-details="auto"
                label="Total weight"
                variant="outlined"
              />
            </v-col>
            <v-col>
              <omni-select
                v-model="actualFreight.bolIds"
                :disabled="isBolReadonly || loadData.bolNotAvailable"
                :items="bolFreightOptions"
                :required="isFreightBolRequired"
                :rules="freightBolRules"
                class="bg-white"
                data-qa="actual-freight-bol-select"
                label="BOL"
                multiple
              ></omni-select>
            </v-col>
          </v-row>
        </div>
      </div>
      <div>
        <div class="text-grey-darken-3 font-weight-500 mt-4 mb-2 text-right" data-qa="total-planned-freigth">
          Planned total: {{ freightsQuantity }}, {{ freightsWeight }} lbs
        </div>
        <div class="font-weight-500 text-grey-darken-3 text-right" data-qa="total-freight">
          Total:
          <span :class="actualPalletsQuantityClass">{{ actualPalletsQuantity }}</span> plt,
          <span :class="actualPiecesQuantityClass">{{ actualPiecesQuantity }}</span> pcs,
          <span :class="actualFreightsWeightClass">{{ actualFreightsWeight }}</span> lbs
        </div>
      </div>
    </div>

    <div>
      <div class="font-size-16 font-weight-500 mb-4 text-grey-navy">Time</div>
      <v-row>
        <v-col cols="3">
          <timezone-select
            v-model="loadData.timezone"
            :disabled="readonly"
            :items="timezonesShortList"
            :rules="[requiredValidator]"
            required
          />
        </v-col>
        <v-col cols="6">
          <date-picker v-model="loadData.date" :disabled="readonly" label="Date" required></date-picker>
        </v-col>
        <v-col cols="3">
          <time-picker v-model="loadData.time" :disabled="readonly" label="Time" required></time-picker>
        </v-col>
      </v-row>
    </div>
  </v-form>
</template>

<style lang="scss" scoped></style>
